import type { FetchError } from 'ofetch'
import { apiErrorDetails } from '~/utils/apiError'
import {
  v1WebReportControllerCreate,
  v1WebReportControllerGet,
  v1WebReportControllerGetPublicReport,
  v1WebReportControllerListAccess,
  v1WebReportControllerPublish,
  v1WebReportControllerRenewAccess,
  v1WebReportControllerResendAccess,
  v1WebReportControllerRevokeAccess,
  v1WebReportControllerUpdateTemplate,
} from '~/generated/api/sdk.gen'
import type {
  MapMarkerProjectResponse,
  SiteDto,
  WebReportAccessResponse,
} from '~/generated/api'
import type { PreferredLanguage, Project } from '~/models'
import { sortWebReportSections } from '~/utils/webReportSections'

export type WebReportSectionType =
  | 'cover'
  | 'about'
  | 'client_overview'
  | 'assessment_method'
  | 'matrix'
  | 'severity_definitions'
  | 'assessment_overview'
  | 'summary'
  | 'conclusion'

export interface WebReportSectionTemplate {
  id: string
  type: WebReportSectionType
  title: string
  order: number
  visible: boolean
  highlighted: boolean
  comment: string
  importantAreaIds?: string[]
  areaOrderIds?: string[]
  hiddenAreaIds?: string[]
  publishedLanguageId?: string
  publishedLanguageTag?: string
  publishedProtectasBrand?: boolean
  learnMoreUrl?: string
}

export type WebReportAccessStatus = 'active' | 'expired' | 'revoked'

export type PublicAccessErrorReason =
  | 'expired'
  | 'revoked'
  | 'not_found'
  | 'unknown'

/**
 * Why a public report link did not open. The API answers 410 for both an
 * expired and a revoked link and tells them apart by error code; public
 * clients never receive the error message.
 */
export function classifyPublicAccessError(err: unknown): PublicAccessErrorReason {
  const { statusCode, code } = apiErrorDetails(err)
  if (code === 'report_access_revoked')
    return 'revoked'
  if (code === 'report_access_expired')
    return 'expired'
  if (code === 'report_not_found' || statusCode === 404)
    return 'not_found'
  return 'unknown'
}

/**
 * Whether a project's web report has not been created yet, as opposed to the
 * project being missing or withheld. An API without error codes answers both
 * with a bare 404.
 */
export function isWebReportNotCreated(err: unknown): boolean {
  const { statusCode, code } = apiErrorDetails(err)
  return statusCode === 404 && (code === 'web_report_not_created' || code === undefined)
}

export interface WebReportAccessEntry {
  id: string
  recipientEmail: string
  accessToken: string
  expiresAt: string
  revokedAt: string | null
  openedAt: string | null
  viewCount: number
  status: WebReportAccessStatus
  createdAt: string
}

function normalizeAccessEntry(
  entry: WebReportAccessResponse,
): WebReportAccessEntry {
  return {
    ...entry,
    revokedAt: entry.revokedAt ?? null,
    openedAt: entry.openedAt ?? null,
  }
}

export interface PublishRecipient {
  email: string
  expiresAt: string
}

interface PublishLanguage {
  id?: string
  tag?: string
}

interface PublishMetadata {
  language?: PublishLanguage
  protectasBrand?: boolean
}

const REMOVED_SECTION_TYPES = new Set(['scenarios', 'severity_definitions'])

export function isRemovedWebReportSection(section: {
  type: string
  title?: string
}): boolean {
  const type = section.type?.toLowerCase() ?? ''
  const title = section.title?.trim().toLowerCase() ?? ''
  return (
    REMOVED_SECTION_TYPES.has(type)
    || title === 'scenarios'
    || title === 'severity definitions'
  )
}

function ensureAssessmentMethodSection(
  sections: WebReportSectionTemplate[],
): WebReportSectionTemplate[] {
  if (sections.some(section => section.type === 'assessment_method'))
    return sections

  const clientOverviewIndex = sections.findIndex(
    section => section.type === 'client_overview',
  )
  const insertAt
    = clientOverviewIndex >= 0 ? clientOverviewIndex + 1 : sections.length

  const assessmentMethodSection: WebReportSectionTemplate = {
    id: 'assessment-method',
    type: 'assessment_method',
    title: 'Assessment method',
    order: insertAt,
    visible: true,
    highlighted: false,
    comment: '',
  }

  const next = [...sections]
  next.splice(insertAt, 0, assessmentMethodSection)
  return next
}

function ensureConclusionSection(
  sections: WebReportSectionTemplate[],
): WebReportSectionTemplate[] {
  if (sections.some(section => section.type === 'conclusion'))
    return sections

  const summaryIndex = sections.findIndex(
    section => section.type === 'summary',
  )
  const insertAt = summaryIndex >= 0 ? summaryIndex + 1 : sections.length

  const conclusionSection: WebReportSectionTemplate = {
    id: 'conclusion',
    type: 'conclusion',
    title: 'Conclusion',
    order: insertAt,
    visible: true,
    highlighted: false,
    comment: '',
  }

  const next = [...sections]
  next.splice(insertAt, 0, conclusionSection)
  return next
}

export function normalizeWebReportTemplate(
  sections: Array<WebReportSectionTemplate & { type: string }>,
): WebReportSectionTemplate[] {
  const filtered = sections.filter(
    section => !isRemovedWebReportSection(section),
  )
  const withAssessmentMethod = ensureAssessmentMethodSection(filtered)
  const withConclusion = ensureConclusionSection(withAssessmentMethod)
  return sortWebReportSections(withConclusion)
}

export interface PublicOpportunityProject {
  project: Project
  webReportId?: string | null
  template: WebReportSectionTemplate[]
  publishedTemplate?: WebReportSectionTemplate[] | null
  lastPublishedAt?: string | null
  hasCoverImage?: boolean
}

export interface PublicReportData {
  webReport: {
    id: string
    template: WebReportSectionTemplate[]
    publishedTemplate?: WebReportSectionTemplate[] | null
    lastPublishedAt: string | null
    project: { id: string, name: string }
  }
  access: {
    id: string
    expiresAt: string
    openedAt: string | null
    viewCount: number
  }
  project: Project
  opportunityProjects: MapMarkerProjectResponse[]
  accountSites: SiteDto[]
  opportunityScopeProjects: PublicOpportunityProject[]
  hasCoverImage?: boolean
  contactEmail?: string
  language?: PreferredLanguage | null
  contentTranslations?: Record<string, string>
  uiLocalizations?: Record<string, string>
  protectasBrand?: boolean
}

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object')
    return false

  const candidate = error as {
    name?: string
    cause?: { name?: string }
  }
  return candidate.name === 'AbortError' || candidate.cause?.name === 'AbortError'
}

export const useWebReportStore = defineStore('webReport', () => {
  const appConfig = useAppConfig()

  const template = ref<WebReportSectionTemplate[]>([])
  const webReportId = ref<string | null>(null)
  const lastPublishedAt = ref<string | null>(null)
  /** Snapshot of the template as of the last publish; null until first published. */
  const publishedTemplate = ref<WebReportSectionTemplate[] | null>(null)
  /** Snapshot of the template as of the last successful load/save; drives the editor's unsaved-change dot. */
  const lastSavedTemplate = ref<WebReportSectionTemplate[] | null>(null)
  const accessEntries = ref<WebReportAccessEntry[]>([])
  const previewMode = ref(false)
  const saving = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const publicAccessErrorReason = ref<PublicAccessErrorReason | null>(null)
  const publicReportData = ref<PublicReportData | null>(null)
  const publicAccessToken = ref<string | null>(null)
  const publicOpportunityProjects = ref<Map<string, PublicOpportunityProject>>(
    new Map(),
  )
  const activeReportProtectasBrand = ref<boolean | null>(null)
  let publicRequest = 0
  let publicSelection = 0

  function publicAssetUrl(path: string, projectId?: string) {
    if (!publicAccessToken.value) {
      return null
    }

    if (path === 'cover-image') {
      const scopedProjectId = projectId ?? publicReportData.value?.project?.id
      if (!scopedProjectId) {
        return null
      }
      return `${appConfig.DEV_ENDPOINT}/web-report/view/${publicAccessToken.value}/cover-image/${scopedProjectId}`
    }

    return `${appConfig.DEV_ENDPOINT}/web-report/view/${publicAccessToken.value}/${path}`
  }

  function clearPublicAccess() {
    publicRequest++
    publicSelection++
    useProjectStore().resetMapData()
    loading.value = false
    publicAccessToken.value = null
    publicReportData.value = null
    publicAccessErrorReason.value = null
    publicOpportunityProjects.value = new Map()
  }

  function cachePublicOpportunityProjects(
    entries: PublicOpportunityProject[] | undefined,
  ) {
    const next = new Map<string, PublicOpportunityProject>()
    for (const entry of entries ?? []) {
      if (entry.project?.id) {
        next.set(entry.project.id, entry)
      }
    }
    publicOpportunityProjects.value = next
  }

  function applyPublicOpportunityProject(
    projectId: string,
    projectStore: ReturnType<typeof useProjectStore>,
  ) {
    const entry = publicOpportunityProjects.value.get(projectId)
    if (!entry) {
      return { generation: projectStore.mapDataGeneration, completion: Promise.resolve(false) }
    }

    const selection = ++publicSelection
    const request = publicRequest
    const { selectedProject } = storeToRefs(projectStore)
    const { fetchProjectCoverImage } = projectStore

    useAssessmentStore().scenarioImages.clear()

    selectedProject.value = { ...entry.project }
    projectStore.setPublicMapData(
      publicReportData.value?.accountSites ?? [],
      publicReportData.value?.opportunityProjects ?? [],
    )
    const generation = projectStore.mapDataGeneration
    const isCurrent = () =>
      selection === publicSelection && request === publicRequest
      && generation === projectStore.mapDataGeneration
    template.value = normalizeWebReportTemplate(entry.template ?? [])
    webReportId.value = entry.webReportId ?? null
    lastPublishedAt.value = entry.lastPublishedAt ?? null

    // Establish ownership synchronously and return it explicitly. Making this
    // phase async would change the return type and fail callers' type checks.
    const finishApplication = async () => {
      if (entry.hasCoverImage) {
        const coverImage = await fetchProjectCoverImage(projectId)
        if (!isCurrent())
          return false
        if (coverImage && selectedProject.value) {
          selectedProject.value = {
            ...selectedProject.value,
            image: coverImage,
          }
        }
      }

      if (!isCurrent() || !selectedProject.value)
        return false

      if (publicReportData.value) {
        publicReportData.value = {
          ...publicReportData.value,
          project: selectedProject.value,
          hasCoverImage: entry.hasCoverImage,
        }
      }

      stripRemovedSections()
      return true
    }

    return { generation, completion: finishApplication() }
  }

  async function switchPublicProject(projectId: string) {
    const projectStore = useProjectStore()
    return applyPublicOpportunityProject(projectId, projectStore).completion
  }

  function reset() {
    template.value = []
    webReportId.value = null
    lastPublishedAt.value = null
    publishedTemplate.value = null
    lastSavedTemplate.value = null
    accessEntries.value = []
    previewMode.value = false
    error.value = null
    activeReportProtectasBrand.value = null
    clearPublicAccess()
  }

  async function fetchOrCreate(projectId: string) {
    loading.value = true
    error.value = null
    try {
      // Try to get existing
      let data = null
      try {
        const response = await v1WebReportControllerGet({
          path: { projectId },
          throwOnError: true,
        })
        data = response.data
      }
      catch (err) {
        if (isWebReportNotCreated(err)) {
          // Create with default template
          const response = await v1WebReportControllerCreate({
            path: { projectId },
            throwOnError: true,
          })
          data = response.data
        }
        else {
          throw err
        }
      }
      if (data) {
        webReportId.value = data.id
        template.value = normalizeWebReportTemplate(
          (data.template as unknown as WebReportSectionTemplate[] | undefined)
          ?? [],
        )
        lastPublishedAt.value
          = data.lastPublishedAt ?? null
        publishedTemplate.value = (data.publishedTemplate as unknown as WebReportSectionTemplate[] | undefined) ?? null
        lastSavedTemplate.value = template.value.map(section => ({
          ...section,
        }))
      }
    }
    catch (err) {
      error.value = (err as Error).message
    }
    finally {
      loading.value = false
    }
  }

  async function saveTemplate(
    projectId: string,
    options?: { signal?: AbortSignal },
  ) {
    if (!webReportId.value)
      return

    saving.value = true
    try {
      const submittedTemplate = template.value
      const { data } = await v1WebReportControllerUpdateTemplate({
        body: { sections: submittedTemplate },
        path: { projectId },
        signal: options?.signal,
        throwOnError: true,
      })
      if (data) {
        const submittedById = new Map(
          submittedTemplate.map(section => [section.id, section]),
        )
        template.value = normalizeWebReportTemplate(
          (data.template as unknown as WebReportSectionTemplate[] | undefined)
          ?? [],
        ).map((section) => {
          if (section.hiddenAreaIds !== undefined)
            return section

          return {
            ...section,
            hiddenAreaIds: submittedById.get(section.id)?.hiddenAreaIds,
          }
        })
        lastSavedTemplate.value = template.value.map(section => ({
          ...section,
        }))
      }
    }
    catch (err) {
      if (isAbortError(err))
        return
      throw err
    }
    finally {
      saving.value = false
    }
  }

  async function publish(
    projectId: string,
    recipients: PublishRecipient[],
    metadata?: PublishMetadata,
  ): Promise<WebReportAccessEntry[]> {
    const { data } = await v1WebReportControllerPublish({
      body: {
        recipients,
        languageId: metadata?.language?.id,
        languageTag: metadata?.language?.tag,
        protectasBrand: metadata?.protectasBrand,
      },
      path: { projectId },
      throwOnError: true,
    })
    lastPublishedAt.value = new Date().toISOString()
    publishedTemplate.value = template.value.map(section => ({ ...section }))
    lastSavedTemplate.value = template.value.map(section => ({ ...section }))
    const newEntries = (data?.access ?? []).map(normalizeAccessEntry)
    accessEntries.value = [...newEntries, ...accessEntries.value]
    return newEntries
  }

  async function fetchAccessEntries(projectId: string) {
    const { data } = await v1WebReportControllerListAccess({
      path: { projectId },
      throwOnError: true,
    })
    accessEntries.value = (data?.access ?? []).map(normalizeAccessEntry)
  }

  async function revokeEntry(accessId: string) {
    await v1WebReportControllerRevokeAccess({
      path: { accessId },
      throwOnError: true,
    })
    const entry = accessEntries.value.find(e => e.id === accessId)
    if (entry) {
      entry.revokedAt = new Date().toISOString()
      entry.status = 'revoked'
    }
  }

  async function renewEntry(accessId: string, newExpiry: string) {
    const { data } = await v1WebReportControllerRenewAccess({
      body: { expiresAt: newExpiry },
      path: { accessId },
      throwOnError: true,
    })
    const idx = accessEntries.value.findIndex(e => e.id === accessId)
    if (idx !== -1 && data) {
      accessEntries.value[idx] = normalizeAccessEntry(data)
    }
  }

  async function resendEntry(accessId: string) {
    await v1WebReportControllerResendAccess({
      path: { accessId },
      throwOnError: true,
    })
  }

  async function fetchPublicReport(accessToken: string) {
    clearPublicAccess()
    loading.value = true
    error.value = null
    publicAccessToken.value = accessToken

    const projectStore = useProjectStore()
    const request = publicRequest
    let generation = projectStore.mapDataGeneration
    const isCurrent = () =>
      request === publicRequest && generation === projectStore.mapDataGeneration

    try {
      const response = await v1WebReportControllerGetPublicReport({
        path: { accessToken },
        security: [],
        throwOnError: true,
      })
      if (!isCurrent())
        return
      const data = response.data as unknown as PublicReportData
      publicReportData.value = data
      cachePublicOpportunityProjects(data.opportunityScopeProjects)

      if (data?.project?.id) {
        const application = applyPublicOpportunityProject(data.project.id, projectStore)
        generation = application.generation
        await application.completion
      }
      else if (data?.webReport?.template) {
        template.value = normalizeWebReportTemplate(data.webReport.template)
      }
    }
    catch (err) {
      if (!isCurrent())
        return
      clearPublicAccess()
      const fetchError = err as FetchError
      error.value
        = (fetchError.data as { message?: string } | undefined)?.message
          ?? fetchError.message
          ?? 'Report not found or access expired'
      publicAccessErrorReason.value = classifyPublicAccessError(fetchError)
    }
    finally {
      if (request === publicRequest)
        loading.value = false
    }
  }

  function updateSection(
    sectionId: string,
    updates: Partial<WebReportSectionTemplate>,
  ) {
    const idx = template.value.findIndex(s => s.id === sectionId)
    if (idx !== -1) {
      template.value[idx] = { ...template.value[idx], ...updates }
    }
  }

  function setTemplatePublishedMetadata(metadata?: PublishMetadata | null) {
    template.value = template.value.map(section => ({
      ...section,
      publishedLanguageId: metadata?.language?.id,
      publishedLanguageTag: metadata?.language?.tag,
      publishedProtectasBrand: metadata?.protectasBrand,
    }))
  }

  function reorderSections(newOrder: WebReportSectionTemplate[]) {
    template.value = normalizeWebReportTemplate(
      newOrder.map((s, i) => ({ ...s, order: i })),
    )
  }

  function stripRemovedSections() {
    const normalized = normalizeWebReportTemplate(template.value)
    if (normalized.length !== template.value.length)
      template.value = normalized
  }

  return {
    template,
    webReportId,
    lastPublishedAt,
    publishedTemplate,
    lastSavedTemplate,
    accessEntries,
    previewMode,
    saving,
    loading,
    error,
    publicAccessErrorReason,
    publicReportData,
    publicAccessToken,
    publicOpportunityProjects,
    activeReportProtectasBrand,
    publicAssetUrl,
    clearPublicAccess,
    switchPublicProject,
    reset,
    fetchOrCreate,
    saveTemplate,
    publish,
    fetchAccessEntries,
    revokeEntry,
    renewEntry,
    resendEntry,
    fetchPublicReport,
    updateSection,
    setTemplatePublishedMetadata,
    reorderSections,
    stripRemovedSections,
  }
})
